import React from 'react';
import PropTypes from 'prop-types';
import {Icon} from '@/components/atoms';
import {COLOR_CODE} from '@/utils/constants/colors';
import {
  ACTIVITY_SEGMENTS_WARNING_MESSAGE,
  NO_SEGMENTS_SELECTED
} from '@/utils/constants/commonConstants';
import {
  formatTagsCommaSeparated,
  getSegmentGroupsForReview
} from '@/utils/activityReviewUtils';

const formatBooleanAnswer = (value) => (value ? 'Yes' : 'No');

const SEGMENT_GROUPS_GRID_CLASS =
  'mt-3 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3';

const SegmentGroupCell = ({label, value, dataTestId}) => (
  <div className='pb-2' data-testid={dataTestId}>
    <p className='text-sm text-[#D0D0D0]'>{label}</p>
    <p className='font-bold text-white'>{value}</p>
  </div>
);

SegmentGroupCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  dataTestId: PropTypes.string
};

const ActivitySegmentsReviewSection = ({
  activityFormData,
  isExcluded,
  RowComponent,
  SectionTitleComponent,
  testIdPrefix,
  sectionTitleClassName = 'mb-3 mt-6',
  showAllCustomersRow = false,
  showExcludedRow = false,
  beforeGroups = null
}) => {
  const segmentGroups = getSegmentGroupsForReview(
    activityFormData?.selectedSegments
  );
  const excludedSegmentsValue = formatTagsCommaSeparated(
    activityFormData?.excludedSegments,
    {emptyFallback: '-'}
  );

  return (
    <>
      <SectionTitleComponent
        label='Segments'
        className={sectionTitleClassName}
        showDivider
      />

      {beforeGroups}

      {showAllCustomersRow && (
        <RowComponent
          label='All Customers'
          value={formatBooleanAnswer(activityFormData?.allCustomers)}
          dataTestId={`${testIdPrefix}-all-customers-row`}
        />
      )}

      {!activityFormData?.allCustomers && (
        <>
          {segmentGroups.length > 0 ? (
            <div
              className={SEGMENT_GROUPS_GRID_CLASS}
              data-testid={`${testIdPrefix}-segment-groups-grid`}>
              {segmentGroups.map((group) => (
                <SegmentGroupCell
                  key={group.label}
                  label={group.label}
                  value={group.value}
                  dataTestId={`${testIdPrefix}-segment-group-${group.label.replace(/\s+/g, '-').toLowerCase()}`}
                />
              ))}
            </div>
          ) : (
            <RowComponent
              label='Segments'
              value={NO_SEGMENTS_SELECTED}
              dataTestId={`${testIdPrefix}-segments-empty-row`}
            />
          )}
        </>
      )}

      {showExcludedRow && (
        <RowComponent
          label='Excluded'
          value={formatBooleanAnswer(isExcluded)}
          dataTestId={`${testIdPrefix}-excluded-row`}
        />
      )}

      {isExcluded && (
        <RowComponent
          label='Excluded Segments'
          value={excludedSegmentsValue}
          dataTestId={`${testIdPrefix}-excluded-segments-row`}
        />
      )}

      <div
        className='mt-2 flex pb-2 pl-1'
        style={{color: COLOR_CODE._D0D0D0}}
        data-testid={`${testIdPrefix}-segments-info-banner`}>
        <Icon
          icon='info'
          size='small'
          data-testid={`${testIdPrefix}-segments-info-icon`}
        />
        <span className='ml-2'>{ACTIVITY_SEGMENTS_WARNING_MESSAGE}</span>
      </div>
    </>
  );
};

ActivitySegmentsReviewSection.propTypes = {
  activityFormData: PropTypes.shape({
    allCustomers: PropTypes.bool,
    selectedSegments: PropTypes.array,
    excludedSegments: PropTypes.array
  }).isRequired,
  isExcluded: PropTypes.bool.isRequired,
  RowComponent: PropTypes.elementType.isRequired,
  SectionTitleComponent: PropTypes.elementType.isRequired,
  testIdPrefix: PropTypes.string.isRequired,
  sectionTitleClassName: PropTypes.string,
  showAllCustomersRow: PropTypes.bool,
  showExcludedRow: PropTypes.bool,
  beforeGroups: PropTypes.node
};

export default ActivitySegmentsReviewSection;
